import { useCurrentStateAndParams } from '@uirouter/react';
import {
  AlertTriangle,
  Code,
  Gauge,
  Layers,
  History,
  LineChart,
  ScrollText,
} from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useNamespaceAccessRedirect } from '@/react/kubernetes/namespaces/hooks/useNamespaceAccessRedirect';
import { useEnvironmentObservability } from '@/react/kubernetes/namespaces/queries/useEnvironmentObservability';

import { PageHeader } from '@@/PageHeader';
import { WidgetTabs } from '@@/Widget';
import { useCurrentTabIndex, Tab } from '@@/Widget/WidgetTabs';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { useEventWarningsCount } from '../../queries/useEvents';
import { NamespaceYAMLEditor } from '../components/NamespaceYamlEditor';
import { ResourceEventsDatatable } from '../../components/EventsDatatable/ResourceEventsDatatable';
import {
  ObservabilityLogs,
  LOGS_TAB_WRAPPER_CLASS,
} from '../components/Observability/ObservabilityLogs';
import { ObservabilityMetrics } from '../components/Observability/ObservabilityMetrics';

import { UpdateNamespaceForm } from './UpdateNamespaceForm';
import { NamespaceAppsDatatable } from './NamespaceAppsDatatable';
import { ResourceQuotasDatatable } from './ResourceQuotasDatatable/ResourceQuotasDatatable';

export function NamespaceView() {
  const stateAndParams = useCurrentStateAndParams();
  const {
    params: { id: namespace },
  } = stateAndParams;
  useNamespaceAccessRedirect(namespace, {
    to: 'kubernetes.resourcePools',
  });

  const environmentId = useEnvironmentId();
  const eventWarningCount = useEventWarningsCount(environmentId, { namespace });
  const observabilityEnabled = Boolean(
    useEnvironmentObservability(environmentId).data?.enabled
  );

  const tabs: Tab[] = [
    {
      name: 'Namespace',
      icon: Layers,
      widget: <UpdateNamespaceForm />,
      selectedTabParam: 'namespace',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Events
          {eventWarningCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {eventWarningCount}
            </Badge>
          )}
        </div>
      ),
      icon: History,
      widget: (
        <ResourceEventsDatatable
          namespace={namespace}
          storageKey="kubernetes.namespace.events"
          noWidget={false}
        />
      ),
      selectedTabParam: 'events',
    },
    {
      name: 'Resource Quotas',
      icon: Gauge,
      widget: <ResourceQuotasDatatable namespace={namespace} />,
      selectedTabParam: 'resource-quotas',
    },
    {
      name: 'YAML',
      icon: Code,
      widget: <NamespaceYAMLEditor />,
      selectedTabParam: 'YAML',
    },
    ...(observabilityEnabled
      ? [
          {
            name: 'Metrics',
            icon: LineChart,
            widget: (
              <ObservabilityMetrics
                environmentId={environmentId}
                namespace={namespace}
              />
            ),
            selectedTabParam: 'metrics',
          },
          {
            name: 'Logs',
            icon: ScrollText,
            widget: (
              <ObservabilityLogs
                environmentId={environmentId}
                namespace={namespace}
              />
            ),
            selectedTabParam: 'logs',
          },
        ]
      : []),
  ];
  const currentTabIndex = useCurrentTabIndex(tabs);

  return (
    <>
      <PageHeader
        title="Namespace details"
        breadcrumbs={[
          { label: 'Namespaces', link: 'kubernetes.resourcePools' },
          namespace,
        ]}
        reload
      />
      <>
        <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
        {['logs', 'metrics'].includes(
          tabs[currentTabIndex].selectedTabParam
        ) ? (
          <div className={LOGS_TAB_WRAPPER_CLASS}>
            {tabs[currentTabIndex].widget}
          </div>
        ) : (
          <>
            <div className="mb-4">{tabs[currentTabIndex].widget}</div>
            <NamespaceAppsDatatable namespace={namespace} />
          </>
        )}
      </>
    </>
  );
}
